export interface FormField {
  type:
    | "input"
    | "select"
    | "textarea"
    | "multiselect"
    | "datepicker"
    | "toggle";
  label: string;
  key: string;
  placeholder?: string;
  options?: { label: string; value: any }[];
  required?: boolean;
  rules?: string;
}

export interface FormStep {
  title: string;
  fields: FormField[];
}


export const clientFormSteps: FormStep[] = [
  {
    title: "Basic Information",
    fields: [
      {
        type: "input",
        label: "Full Name",
        key: "fullName",
        placeholder: "Enter full name",
        required: true,
        rules: "required",
      },
      {
        type: "input",
        label: "Client ID",
        key: "clientId",
        placeholder: "Enter client ID",
      },
      {
        type: "datepicker",
        label: "Date of Birth",
        key: "dateOfBirth",
        placeholder: "Select date of birth",
      },
      {
        type: "input",
        label: "Gender",
        key: "gender",
        placeholder: "Enter gender",
      },
      {
        type: "input",
        label: "Address",
        key: "address",
        placeholder: "Enter address",
      },
      {
        type: "datepicker",
        label: "Date Started",
        key: "dateStarted",
        placeholder: "Select date started",
      },
      {
        type: "datepicker",
        label: "Date Completed",
        key: "dateCompleted",
        placeholder: "Select date completed",
      },
      {
        type: "input",
        label: "Phone Number",
        key: "phoneNumber",
        placeholder: "Enter phone number",
      },
      {
        type: "input",
        label: "Email Address",
        key: "emailAddress",
        placeholder: "Enter email address",
      },
      {
        type: "multiselect",
        label: "Tags",
        key: "tags",
        placeholder: "Select tags",
      },
    ],
  },
  {
    title: "Required Services",
    fields: [
      {
        type: "multiselect",
        label: "Support Type",
        key: "support_type",
        placeholder: "Select support types",
      },
      {
        type: "input",
        label: "Frequency",
        key: "frequency",
        placeholder: "Enter frequency",
      },
    ],
  },
  {
    title: "Access Information",
    fields: [
      {
        type: "input",
        label: "Access Type",
        key: "accessType",
        placeholder: "Enter access type",
      },
      {
        type: "input",
        label: "Access Number",
        key: "accessNumber",
        placeholder: "Enter access number",
      },
      {
        type: "textarea",
        label: "Special Instructions",
        key: "specialInstructions",
        placeholder: "Enter special instructions",
      },
    ],
  },
  {
    title: "Emergency Contact",
    fields: [
      {
        type: "input",
        label: "Name",
        key: "name",
        placeholder: "Enter emergency contact name",
      },
      {
        type: "input",
        label: "Relationship",
        key: "relationship",
        placeholder: "Enter relationship",
      },
      {
        type: "input",
        label: "Phone Number",
        key: "phoneNumber",
        placeholder: "Enter phone number",
      },
      {
        type: "input",
        label: "Secondary Contact",
        key: "secondaryContact",
        placeholder: "Enter secondary contact",
      },
      {
        type: "textarea",
        label: "Additional Notes",
        key: "additionalNotes",
        placeholder: "Enter additional notes",
      },
    ],
  },
  {
    title: "Language & Communication",
    fields: [
      {
        type: "multiselect",
        label: "Languages Spoken",
        key: "LanguagesSpoken",
        placeholder: "Select languages",
      },
      {
        type: "input",
        label: "Preferred Language",
        key: "preferredLanguage",
        placeholder: "Enter preferred language",
      },
      {
        type: "toggle",
        label: "Interpreter Needed",
        key: "interpreterNeeded",
      },
      {
        type: "input",
        label: "Communication Style",
        key: "communicationStyle",
        placeholder: "Enter communication style",
      },
    ],
  },
  {
    title: "Health Information",
    fields: [
      {
        type: "input",
        label: "Medical Conditions",
        key: "currentMedicalStatus.medicalConditions",
        placeholder: "Enter current medical conditions",
      },
      {
        type: "input",
        label: "Past Conditions",
        key: "currentMedicalStatus.pastConditions",
        placeholder: "Enter past conditions",
      },
      {
        type: "multiselect",
        label: "Food Allergies",
        key: "allergies.food",
        placeholder: "Select food allergies",
      },
      {
        type: "multiselect",
        label: "Medication Allergies",
        key: "allergies.medication",
        placeholder: "Select medication allergies",
      },
      {
        type: "multiselect",
        label: "Environmental Allergies",
        key: "allergies.environmental",
        placeholder: "Select environmental allergies",
      },
      {
        type: "select",
        label: "DNR Status",
        key: "dnr.status",
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
        ],
      },
    ],
  },
  {
    title: "Functional Assessment",
    fields: [
      {
        type: "select",
        label: "Mobility Status",
        key: "mobilityStatus",
        placeholder: "Enter mobility status",
        options: [
          { label: "Independent", value: "Independent" },
          { label: "Dependent", value: "Dependent" },
        ],
      },
      {
        type: "input",
        label: "Mobility Level",
        key: "mobilityDetails.level",
        placeholder: "Enter mobility level",
      },
      {
        type: "textarea",
        label: "Mobility Notes",
        key: "mobilityDetails.notes",
        placeholder: "Enter mobility notes",
      },
      {
        type: "select",
        label: "Communication Ability",
        key: "communicationAbility",
        placeholder: "Enter communication ability",
        options: [
          { label: "Verbal", value: "Verbal" },
          { label: "Non-Verbal", value: "Non-Verbal" },
        ],
      },
      {
        type: "textarea",
        label: "Communication Notes",
        key: "communicationNotes",
        placeholder: "Enter communication notes",
      },
      {
        type: "input",
        label: "Cognitive Concerns",
        key: "cognitiveStatus.concerns",
        placeholder: "Enter cognitive concerns",
      },
      {
        type: "textarea",
        label: "Cognitive Notes",
        key: "cognitiveStatus.notes",
        placeholder: "Enter cognitive notes",
      },
    ],
  },
  {
    title: "Personal Considerations",
    fields: [
      {
        type: "multiselect",
        label: "Pets",
        key: "homeEnvironment.pets",
        placeholder: "Select pets",
      },
      {
        type: "input",
        label: "Accessibility Issues",
        key: "homeEnvironment.accessibilityIssues",
        placeholder: "Enter accessibility issues",
      },
      {
        type: "input",
        label: "Safety Concerns",
        key: "homeEnvironment.safetyConcerns",
        placeholder: "Enter safety concerns",
      },
      {
        type: "select",
        label: "Smart Home Features",
        key: "homeEnvironment.smartHomeFeatures",
        placeholder: "Enter smart home features",
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" },
        ],
      },
      {
        type: "multiselect",
        label: "Hobbies",
        key: "personalPreferences.hobbies",
        placeholder: "Select hobbies",
      },
      {
        type: "input",
        label: "Interests",
        key: "personalPreferences.interests",
        placeholder: "Enter interests",
      },
      {
        type: "input",
        label: "Cultural Considerations",
        key: "personalPreferences.culturalConsiderations",
        placeholder: "Enter cultural considerations",
      },
      {
        type: "input",
        label: "Religious Practices",
        key: "personalPreferences.religiousPractices",
        placeholder: "Enter religious practices",
      },
      {
        type: "input",
        label: "Dietary Restrictions",
        key: "personalPreferences.dietaryRestrictions",
        placeholder: "Enter dietary restrictions",
      },
    ],
  },
  {
    title: "Support Network",
    fields: [
      {
        type: "input",
        label: "Family Support",
        key: "familySupport",
        placeholder: "Enter family support details",
      },
      {
        type: "input",
        label: "Social Connections",
        key: "socialConnections",
        placeholder: "Enter social connections",
      },
      {
        type: "input",
        label: "Community Services",
        key: "communityServices",
        placeholder: "Enter community services",
      },
      {
        type: "input",
        label: "Professional Care Contacts",
        key: "professionalCareContacts",
        placeholder: "Enter professional care contacts",
      },
    ],
  },
  {
    title: "Care Goals",
    fields: [
      {
        type: "input",
        label: "Short Term Goals",
        key: "shortTerm",
        placeholder: "Enter short term goals",
      },
      {
        type: "input",
        label: "Long Term Goals",
        key: "longTerm",
        placeholder: "Enter long term goals",
      },
      {
        type: "input",
        label: "Special Care Needs",
        key: "specialCareNeeds",
        placeholder: "Enter special care needs",
      },
      {
        type: "input",
        label: "Outcomes",
        key: "outcomes",
        placeholder: "Enter desired outcomes",
      },
      {
        type: "select",
        label: "Self Directed Care",
        key: "selfDirectedCare",
        placeholder: "Enter self directed care preferences",
        options: [
          {
            label: "Participatory in Planning",
            value: "Participatory in Planning",
          },
          { label: "Self Directed Care", value: "Self Directed Care" },
        ],
      },
    ],
  },
  {
    title: "Additional Notes",
    fields: [
      {
        type: "textarea",
        label: "General Notes",
        key: "generalNotes",
        placeholder: "Enter general notes",
      },
      {
        type: "textarea",
        label: "Special Instructions",
        key: "specialInstructions",
        placeholder: "Enter special instructions",
      },
      {
        type: "textarea",
        label: "Other Notes",
        key: "otherNotes",
        placeholder: "Enter other notes",
      },
    ],
  },
];
export const tabs = [
  {
    title: "Client Profile",
    fields: [
      {
        title: "Basic Information",
      },
    ],
  },
  {
    title: "Client Details",
    showSteps: true,
    fields: [],
    steps: [
      {
        title: "Required Services",
        fields: [
          {
            type: "multiselect",
            label: "Support Type",
            key: "support_type",
            placeholder: "Select support types",
            options: [
              { label: "Personal Care", value: "Personal Care" },
              { label: "Home Care", value: "Home Care" },
              { label: "Respite Care", value: "Respite Care" },
            ],
          },
          {
            type: "select",
            label: "Frequency",
            key: "frequency",
            placeholder: "Enter frequency",
            options: [
              { label: "Daily", value: "Daily" },
              { label: "Weekly", value: "Weekly" },
              { label: "Monthly", value: "Monthly" },
            ],
          },
        ],
      },
      {
        title: "Access Information",
        fields: [
          {
            type: "input",
            label: "Access Type",
            key: "accessType",
            placeholder: "Enter access type",
          },
          {
            type: "input",
            label: "Access Number",
            key: "accessNumber",
            placeholder: "Enter access number",
          },
          {
            type: "textarea",
            label: "Special Instructions",
            key: "specialInstructions",
            placeholder: "Enter special instructions",
          },
        ],
      },
      {
        title: "Emergency Contact",
        fields: [
          {
            type: "input",
            label: "Name",
            key: "name",
            placeholder: "Enter emergency contact name",
          },
          {
            type: "input",
            label: "Relationship",
            key: "relationship",
            placeholder: "Enter relationship",
          },
          {
            type: "input",
            label: "Phone Number",
            key: "phoneNumber",
            placeholder: "Enter phone number",
          },
          {
            type: "input",
            label: "Secondary Contact",
            key: "secondaryContact",
            placeholder: "Enter secondary contact",
          },
          {
            type: "textarea",
            label: "Additional Notes",
            key: "additionalNotes",
            placeholder: "Enter additional notes",
          },
        ],
      },
      {
        title: "Language & Communication",
        fields: [
          {
            type: "multiselect",
            label: "Languages Spoken",
            key: "LanguagesSpoken",
            placeholder: "Select languages",
          },
          {
            type: "input",
            label: "Preferred Language",
            key: "preferredLanguage",
            placeholder: "Enter preferred language",
          },
          {
            type: "toggle",
            label: "Interpreter Needed",
            key: "interpreterNeeded",
          },
          {
            type: "input",
            label: "Communication Style",
            key: "communicationStyle",
            placeholder: "Enter communication style",
          },
        ],
      },
      {
        title: "Health Information",
        fields: [
          {
            type: "input",
            label: "Medical Conditions",
            key: "currentMedicalStatus.medicalConditions",
            placeholder: "Enter current medical conditions",
          },
          {
            type: "input",
            label: "Past Conditions",
            key: "currentMedicalStatus.pastConditions",
            placeholder: "Enter past conditions",
          },
          {
            type: "multiselect",
            label: "Food Allergies",
            key: "allergies.food",
            placeholder: "Select food allergies",
          },
          {
            type: "multiselect",
            label: "Medication Allergies",
            key: "allergies.medication",
            placeholder: "Select medication allergies",
          },
          {
            type: "multiselect",
            label: "Environmental Allergies",
            key: "allergies.environmental",
            placeholder: "Select environmental allergies",
          },
          {
            type: "select",
            label: "DNR Status",
            key: "dnr.status",
            options: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
          },
        ],
      },
      {
        title: "Functional Assessment",
        fields: [
          {
            type: "select",
            label: "Mobility Status",
            key: "mobilityStatus",
            placeholder: "Enter mobility status",
            options: [
              { label: "Independent", value: "Independent" },
              { label: "Dependent", value: "Dependent" },
            ],
          },
          {
            type: "input",
            label: "Mobility Level",
            key: "mobilityDetails.level",
            placeholder: "Enter mobility level",
          },
          {
            type: "textarea",
            label: "Mobility Notes",
            key: "mobilityDetails.notes",
            placeholder: "Enter mobility notes",
          },
          {
            type: "select",
            label: "Communication Ability",
            key: "communicationAbility",
            placeholder: "Enter communication ability",
            options: [
              { label: "Verbal", value: "Verbal" },
              { label: "Non-Verbal", value: "Non-Verbal" },
            ],
          },
          {
            type: "textarea",
            label: "Communication Notes",
            key: "communicationNotes",
            placeholder: "Enter communication notes",
          },
          {
            type: "input",
            label: "Cognitive Concerns",
            key: "cognitiveStatus.concerns",
            placeholder: "Enter cognitive concerns",
          },
          {
            type: "textarea",
            label: "Cognitive Notes",
            key: "cognitiveStatus.notes",
            placeholder: "Enter cognitive notes",
          },
        ],
      },
      {
        title: "Personal Considerations",
        fields: [
          {
            type: "multiselect",
            label: "Pets",
            key: "homeEnvironment.pets",
            placeholder: "Select pets",
          },
          {
            type: "input",
            label: "Accessibility Issues",
            key: "homeEnvironment.accessibilityIssues",
            placeholder: "Enter accessibility issues",
          },
          {
            type: "input",
            label: "Safety Concerns",
            key: "homeEnvironment.safetyConcerns",
            placeholder: "Enter safety concerns",
          },
          {
            type: "select",
            label: "Smart Home Features",
            key: "homeEnvironment.smartHomeFeatures",
            placeholder: "Enter smart home features",
            options: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
          },
          {
            type: "multiselect",
            label: "Hobbies",
            key: "personalPreferences.hobbies",
            placeholder: "Select hobbies",
          },
          {
            type: "input",
            label: "Interests",
            key: "personalPreferences.interests",
            placeholder: "Enter interests",
          },
          {
            type: "input",
            label: "Cultural Considerations",
            key: "personalPreferences.culturalConsiderations",
            placeholder: "Enter cultural considerations",
          },
          {
            type: "input",
            label: "Religious Practices",
            key: "personalPreferences.religiousPractices",
            placeholder: "Enter religious practices",
          },
          {
            type: "input",
            label: "Dietary Restrictions",
            key: "personalPreferences.dietaryRestrictions",
            placeholder: "Enter dietary restrictions",
          },
        ],
      },
      {
        title: "Support Network",
        fields: [
          {
            type: "input",
            label: "Family Support",
            key: "familySupport",
            placeholder: "Enter family support details",
          },
          {
            type: "input",
            label: "Social Connections",
            key: "socialConnections",
            placeholder: "Enter social connections",
          },
          {
            type: "input",
            label: "Community Services",
            key: "communityServices",
            placeholder: "Enter community services",
          },
          {
            type: "input",
            label: "Professional Care Contacts",
            key: "professionalCareContacts",
            placeholder: "Enter professional care contacts",
          },
        ],
      },
      {
        title: "Care Goals",
        fields: [
          {
            type: "input",
            label: "Short Term Goals",
            key: "shortTerm",
            placeholder: "Enter short term goals",
          },
          {
            type: "input",
            label: "Long Term Goals",
            key: "longTerm",
            placeholder: "Enter long term goals",
          },
          {
            type: "input",
            label: "Special Care Needs",
            key: "specialCareNeeds",
            placeholder: "Enter special care needs",
          },
          {
            type: "input",
            label: "Outcomes",
            key: "outcomes",
            placeholder: "Enter desired outcomes",
          },
          {
            type: "select",
            label: "Self Directed Care",
            key: "selfDirectedCare",
            placeholder: "Enter self directed care preferences",
            options: [
              {
                label: "Participatory in Planning",
                value: "Participatory in Planning",
              },
              { label: "Self Directed Care", value: "Self Directed Care" },
            ],
          },
        ],
      },
      {
        title: "Additional Notes",
        fields: [
          {
            type: "textarea",
            label: "General Notes",
            key: "generalNotes",
            placeholder: "Enter general notes",
          },
          {
            type: "textarea",
            label: "Special Instructions",
            key: "specialInstructions",
            placeholder: "Enter special instructions",
          },
          {
            type: "textarea",
            label: "Other Notes",
            key: "otherNotes",
            placeholder: "Enter other notes",
          },
        ],
      },
    ],
  },
  {
    title: "Care Plan",
    showSteps: true,
    fields: [],
    steps: [
      {
        title: "Required Services",
        fields: [
          {
            type: "multiselect",
            label: "Support Type",
            key: "support_type",
            placeholder: "Select support types",
          },
          {
            type: "input",
            label: "Frequency",
            key: "frequency",
            placeholder: "Enter frequency",
          },
        ],
      },
      {
        title: "Access Information",
        fields: [
          {
            type: "input",
            label: "Access Type",
            key: "accessType",
            placeholder: "Enter access type",
          },
          {
            type: "input",
            label: "Access Number",
            key: "accessNumber",
            placeholder: "Enter access number",
          },
          {
            type: "textarea",
            label: "Special Instructions",
            key: "specialInstructions",
            placeholder: "Enter special instructions",
          },
        ],
      },
      {
        title: "Emergency Contact",
        fields: [
          {
            type: "input",
            label: "Name",
            key: "name",
            placeholder: "Enter emergency contact name",
          },
          {
            type: "input",
            label: "Relationship",
            key: "relationship",
            placeholder: "Enter relationship",
          },
          {
            type: "input",
            label: "Phone Number",
            key: "phoneNumber",
            placeholder: "Enter phone number",
          },
          {
            type: "input",
            label: "Secondary Contact",
            key: "secondaryContact",
            placeholder: "Enter secondary contact",
          },
          {
            type: "textarea",
            label: "Additional Notes",
            key: "additionalNotes",
            placeholder: "Enter additional notes",
          },
        ],
      },
      {
        title: "Language & Communication",
        fields: [
          {
            type: "multiselect",
            label: "Languages Spoken",
            key: "LanguagesSpoken",
            placeholder: "Select languages",
          },
          {
            type: "input",
            label: "Preferred Language",
            key: "preferredLanguage",
            placeholder: "Enter preferred language",
          },
          {
            type: "toggle",
            label: "Interpreter Needed",
            key: "interpreterNeeded",
          },
          {
            type: "input",
            label: "Communication Style",
            key: "communicationStyle",
            placeholder: "Enter communication style",
          },
        ],
      },
      {
        title: "Health Information",
        fields: [
          {
            type: "input",
            label: "Medical Conditions",
            key: "currentMedicalStatus.medicalConditions",
            placeholder: "Enter current medical conditions",
          },
          {
            type: "input",
            label: "Past Conditions",
            key: "currentMedicalStatus.pastConditions",
            placeholder: "Enter past conditions",
          },
          {
            type: "multiselect",
            label: "Food Allergies",
            key: "allergies.food",
            placeholder: "Select food allergies",
          },
          {
            type: "multiselect",
            label: "Medication Allergies",
            key: "allergies.medication",
            placeholder: "Select medication allergies",
          },
          {
            type: "multiselect",
            label: "Environmental Allergies",
            key: "allergies.environmental",
            placeholder: "Select environmental allergies",
          },
          {
            type: "select",
            label: "DNR Status",
            key: "dnr.status",
            options: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
          },
        ],
      },
      {
        title: "Functional Assessment",
        fields: [
          {
            type: "select",
            label: "Mobility Status",
            key: "mobilityStatus",
            placeholder: "Enter mobility status",
            options: [
              { label: "Independent", value: "Independent" },
              { label: "Dependent", value: "Dependent" },
            ],
          },
          {
            type: "input",
            label: "Mobility Level",
            key: "mobilityDetails.level",
            placeholder: "Enter mobility level",
          },
          {
            type: "textarea",
            label: "Mobility Notes",
            key: "mobilityDetails.notes",
            placeholder: "Enter mobility notes",
          },
          {
            type: "select",
            label: "Communication Ability",
            key: "communicationAbility",
            placeholder: "Enter communication ability",
            options: [
              { label: "Verbal", value: "Verbal" },
              { label: "Non-Verbal", value: "Non-Verbal" },
            ],
          },
          {
            type: "textarea",
            label: "Communication Notes",
            key: "communicationNotes",
            placeholder: "Enter communication notes",
          },
          {
            type: "input",
            label: "Cognitive Concerns",
            key: "cognitiveStatus.concerns",
            placeholder: "Enter cognitive concerns",
          },
          {
            type: "textarea",
            label: "Cognitive Notes",
            key: "cognitiveStatus.notes",
            placeholder: "Enter cognitive notes",
          },
        ],
      },
      {
        title: "Personal Considerations",
        fields: [
          {
            type: "multiselect",
            label: "Pets",
            key: "homeEnvironment.pets",
            placeholder: "Select pets",
          },
          {
            type: "input",
            label: "Accessibility Issues",
            key: "homeEnvironment.accessibilityIssues",
            placeholder: "Enter accessibility issues",
          },
          {
            type: "input",
            label: "Safety Concerns",
            key: "homeEnvironment.safetyConcerns",
            placeholder: "Enter safety concerns",
          },
          {
            type: "select",
            label: "Smart Home Features",
            key: "homeEnvironment.smartHomeFeatures",
            placeholder: "Enter smart home features",
            options: [
              { label: "Yes", value: "Yes" },
              { label: "No", value: "No" },
            ],
          },
          {
            type: "multiselect",
            label: "Hobbies",
            key: "personalPreferences.hobbies",
            placeholder: "Select hobbies",
          },
          {
            type: "input",
            label: "Interests",
            key: "personalPreferences.interests",
            placeholder: "Enter interests",
          },
          {
            type: "input",
            label: "Cultural Considerations",
            key: "personalPreferences.culturalConsiderations",
            placeholder: "Enter cultural considerations",
          },
          {
            type: "input",
            label: "Religious Practices",
            key: "personalPreferences.religiousPractices",
            placeholder: "Enter religious practices",
          },
          {
            type: "input",
            label: "Dietary Restrictions",
            key: "personalPreferences.dietaryRestrictions",
            placeholder: "Enter dietary restrictions",
          },
        ],
      },
      {
        title: "Support Network",
        fields: [
          {
            type: "input",
            label: "Family Support",
            key: "familySupport",
            placeholder: "Enter family support details",
          },
          {
            type: "input",
            label: "Social Connections",
            key: "socialConnections",
            placeholder: "Enter social connections",
          },
          {
            type: "input",
            label: "Community Services",
            key: "communityServices",
            placeholder: "Enter community services",
          },
          {
            type: "input",
            label: "Professional Care Contacts",
            key: "professionalCareContacts",
            placeholder: "Enter professional care contacts",
          },
        ],
      },
      {
        title: "Care Goals",
        fields: [
          {
            type: "input",
            label: "Short Term Goals",
            key: "shortTerm",
            placeholder: "Enter short term goals",
          },
          {
            type: "input",
            label: "Long Term Goals",
            key: "longTerm",
            placeholder: "Enter long term goals",
          },
          {
            type: "input",
            label: "Special Care Needs",
            key: "specialCareNeeds",
            placeholder: "Enter special care needs",
          },
          {
            type: "input",
            label: "Outcomes",
            key: "outcomes",
            placeholder: "Enter desired outcomes",
          },
          {
            type: "select",
            label: "Self Directed Care",
            key: "selfDirectedCare",
            placeholder: "Enter self directed care preferences",
            options: [
              {
                label: "Participatory in Planning",
                value: "Participatory in Planning",
              },
              { label: "Self Directed Care", value: "Self Directed Care" },
            ],
          },
        ],
      },
      {
        title: "Additional Notes",
        fields: [
          {
            type: "textarea",
            label: "General Notes",
            key: "generalNotes",
            placeholder: "Enter general notes",
          },
          {
            type: "textarea",
            label: "Special Instructions",
            key: "specialInstructions",
            placeholder: "Enter special instructions",
          },
          {
            type: "textarea",
            label: "Other Notes",
            key: "otherNotes",
            placeholder: "Enter other notes",
          },
        ],
      },
    ],
  },
];
